import { useEffect, useState, useCallback } from 'react'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../supabaseClient'
import {
  Plus, Calendar, Link, X, Check, UserPlus, Shield, User, Pencil,
  Circle, CheckCircle2, AlertCircle, ChevronRight, ChevronDown, Flag,
  Lock, Eye, EyeOff,
} from 'lucide-react'
import ColorPicker from '../../components/ColorPicker/ColorPicker'

/* ─── Costanti task ─── */
const PRIORITA = {
  alta:  { label: 'Alta',  dot: 'bg-red-400',   text: 'text-red-400'   },
  media: { label: 'Media', dot: 'bg-yellow-400', text: 'text-yellow-400'},
  bassa: { label: 'Bassa', dot: 'bg-sky-400',    text: 'text-sky-400'   },
}
const PRIORITA_ORDER = { alta: 0, media: 1, bassa: 2 }

const isScaduta = (task) => {
  if (!task.scadenza || task.status === 'done') return false
  const d = new Date(task.scadenza + 'T00:00:00')
  const oggi = new Date(); oggi.setHours(0, 0, 0, 0)
  return d < oggi
}
const formatDate = (d) => {
  if (!d) return null
  return new Date(d + 'T00:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })
}

/* ─── AvatarStack ─── */
const AvatarStack = ({ assignees, max = 3 }) => {
  if (!assignees?.length) return null
  const shown = assignees.slice(0, max)
  const rest = assignees.length - shown.length
  return (
    <div className="flex -space-x-1.5">
      {shown.map((a, i) => (
        <div key={a.id} title={a.nickname || a.full_name || '?'}
          style={{ zIndex: shown.length - i }}
          className="w-5 h-5 rounded-full bg-violet-600 border border-gray-900 flex items-center justify-center text-white text-[10px] font-bold overflow-hidden">
          {a.avatar_url
            ? <img src={a.avatar_url} alt="" className="w-full h-full object-cover" />
            : (a.nickname || a.full_name || '?')[0].toUpperCase()}
        </div>
      ))}
      {rest > 0 && <div className="w-5 h-5 rounded-full bg-gray-700 border border-gray-900 flex items-center justify-center text-gray-400 text-[10px] font-bold">+{rest}</div>}
    </div>
  )
}

/* ─── TaskRow (progetto detail) ─── */
const TaskRowDetail = ({ task, allTasks, allAssignees, profiles, onToggle, onEdit, depth = 0 }) => {
  const [expanded, setExpanded] = useState(true)
  const hasChildren = allTasks.some(t => t.parent_id === task.id)
  const scaduta = isScaduta(task)
  const p = PRIORITA[task.priorita] || PRIORITA.media
  const isDone = task.status === 'done'
  const assigneeIds = allAssignees.filter(a => a.task_id === task.id).map(a => a.user_id)
  const assignees = profiles.filter(pr => assigneeIds.includes(pr.id))

  return (
    <div>
      <div
        className={`flex items-center gap-2 py-2 px-2 rounded-xl hover:bg-gray-800/60 group transition-colors cursor-pointer ${isDone ? 'opacity-50' : ''}`}
        style={task.colore ? { borderLeft: `3px solid ${task.colore}`, paddingLeft: '6px', borderRadius: '0 12px 12px 0' } : {}}
        onClick={() => onEdit(task)}
      >
        <button onClick={e => { e.stopPropagation(); setExpanded(!expanded) }}
          className={`flex-shrink-0 text-gray-600 w-4 ${hasChildren ? 'visible' : 'invisible'}`}>
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <button onClick={e => { e.stopPropagation(); onToggle(task) }} className="flex-shrink-0">
          {isDone ? <CheckCircle2 size={17} className="text-green-500" /> : <Circle size={17} className="text-gray-600 hover:text-gray-400" />}
        </button>
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${p.dot}`} />
        <span className={`flex-1 text-sm truncate ${isDone ? 'line-through text-gray-500' : 'text-white'}`}>{task.titolo}</span>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {task.facoltativa && (
            <span className="text-[10px] text-gray-500 italic border border-gray-700 rounded px-1 py-px leading-none">
              facolt.
            </span>
          )}
          {scaduta
            ? <span className="flex items-center gap-1 text-xs text-red-400"><AlertCircle size={11} />{formatDate(task.scadenza)}</span>
            : task.scadenza ? <span className="text-xs text-gray-500">{formatDate(task.scadenza)}</span> : null}
          <AvatarStack assignees={assignees} />
        </div>
      </div>
      {expanded && hasChildren && (
        <div className="ml-6 border-l border-gray-800 pl-3">
          {allTasks.filter(t => t.parent_id === task.id).map(child => (
            <TaskRowDetail key={child.id} task={child} allTasks={allTasks} allAssignees={allAssignees} profiles={profiles} onToggle={onToggle} onEdit={onEdit} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  )
}

/* ─── TaskModal inline (per ProgettoDetailPanel) ─── */
const TaskModalInline = ({ task, progettoId, profiles, onClose, onSaved, onDeleted }) => {
  const { profile } = useAuth()
  const isEdit = !!task?.id
  const [titolo, setTitolo] = useState(task?.titolo || '')
  const [descrizione, setDescrizione] = useState(task?.descrizione || '')
  const [priorita, setPriorita] = useState(task?.priorita || 'media')
  const [status, setStatus] = useState(task?.status || 'todo')
  const [scadenza, setScadenza] = useState(task?.scadenza || '')
  const [selectedAssignees, setSelectedAssignees] = useState([])
  const [deliverable, setDeliverable] = useState(task?.deliverable || false)
  const [delivered, setDelivered] = useState(task?.delivered || false)
  const [facoltativa, setFacoltativa] = useState(task?.facoltativa || false)
  const [colore, setColore] = useState(task?.colore || '')
  const [loading, setLoading] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [subtasks, setSubtasks] = useState([])
  const [newSubtask, setNewSubtask] = useState('')

  useEffect(() => {
    if (!isEdit) return
    supabase.from('tasks').select('*').eq('parent_id', task.id).order('created_at').then(({ data }) => setSubtasks(data || []))
    supabase.from('task_assignees').select('user_id').eq('task_id', task.id).then(({ data }) => setSelectedAssignees((data || []).map(a => a.user_id)))
  }, [isEdit, task?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!titolo.trim()) return
    setLoading(true)
    const payload = {
      titolo: titolo.trim(), descrizione: descrizione || null,
      priorita, status, scadenza: scadenza || null,
      project_id: progettoId,
      colore: colore || null,
      deliverable, delivered: deliverable ? delivered : false,
      facoltativa,
      updated_at: new Date().toISOString(),
    }
    let data, err
    if (isEdit) {
      ;({ data, error: err } = await supabase.from('tasks').update(payload).eq('id', task.id).select().single())
    } else {
      ;({ data, error: err } = await supabase.from('tasks').insert({ ...payload, creato_da: profile.id, parent_id: task?.parent_id || null }).select().single())
    }
    if (err) { setLoading(false); return }
    await supabase.from('task_assignees').delete().eq('task_id', data.id)
    if (selectedAssignees.length) await supabase.from('task_assignees').insert(selectedAssignees.map(uid => ({ task_id: data.id, user_id: uid })))
    onSaved(data, selectedAssignees)
    onClose()
  }

  const handleDelete = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return }
    await supabase.from('tasks').delete().eq('id', task.id)
    onDeleted(task.id); onClose()
  }

  const handleAddSubtask = async () => {
    if (!newSubtask.trim()) return
    const { data } = await supabase.from('tasks').insert({
      titolo: newSubtask.trim(), parent_id: task.id,
      project_id: progettoId, creato_da: profile.id, priorita: 'media', status: 'todo',
    }).select().single()
    if (data) { setSubtasks(prev => [...prev, data]); onSaved(data, []) }
    setNewSubtask('')
  }

  const inputCls = "w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500 transition-colors"

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center px-4" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-gray-900 border-b border-gray-800 px-5 py-4 flex items-center justify-between rounded-t-2xl">
          <h3 className="text-white font-semibold">{isEdit ? 'Modifica task' : 'Nuova task'}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <input autoFocus type="text" value={titolo} onChange={e => setTitolo(e.target.value)} required placeholder="Titolo task..." className={inputCls} />
          <textarea value={descrizione} onChange={e => setDescrizione(e.target.value)} rows={2} placeholder="Descrizione..." className={`${inputCls} resize-none`} />

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Priorità</label>
              <select value={priorita} onChange={e => setPriorita(e.target.value)} className={inputCls}>
                <option value="alta">Alta</option><option value="media">Media</option><option value="bassa">Bassa</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Stato</label>
              <select value={status} onChange={e => setStatus(e.target.value)} className={inputCls}>
                <option value="todo">Da fare</option><option value="inprogress">In corso</option><option value="done">Fatto</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Scadenza</label>
              <input type="date" value={scadenza} onChange={e => setScadenza(e.target.value)} className={inputCls} />
            </div>
          </div>

          {/* Assegnato a */}
          <div>
            <label className="text-xs text-gray-500 mb-2 block">Assegnato a</label>
            <div className="flex flex-wrap gap-2">
              {profiles.map(pr => {
                const sel = selectedAssignees.includes(pr.id)
                const nome = pr.nickname || pr.full_name || 'Utente'
                return (
                  <button key={pr.id} type="button" onClick={() => setSelectedAssignees(sel ? selectedAssignees.filter(id => id !== pr.id) : [...selectedAssignees, pr.id])}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs border transition-colors ${sel ? 'bg-violet-600/20 border-violet-500 text-violet-300' : 'border-gray-700 text-gray-400 hover:border-gray-600'}`}>
                    <div className="w-4 h-4 rounded-full bg-violet-600 flex items-center justify-center text-white text-[10px] font-bold overflow-hidden">
                      {pr.avatar_url ? <img src={pr.avatar_url} alt="" className="w-full h-full object-cover" /> : nome[0].toUpperCase()}
                    </div>
                    {nome}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Colore etichetta */}
          <div className="bg-gray-800/50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-400">Colore etichetta</span>
              {colore && (
                <button type="button" onClick={() => setColore('')} className="text-xs text-gray-600 hover:text-gray-400 transition-colors">
                  Rimuovi
                </button>
              )}
            </div>
            <ColorPicker value={colore || '#7c3aed'} onChange={setColore} label="" />
          </div>

          {/* Deliverable + Facoltativa */}
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={deliverable} onChange={e => setDeliverable(e.target.checked)} className="w-4 h-4 accent-orange-500" />
              <span className="text-sm text-gray-400 flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-orange-400" />Deliverable</span>
            </label>
            {deliverable && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={delivered} onChange={e => setDelivered(e.target.checked)} className="w-4 h-4 accent-green-500" />
                <span className="text-sm text-gray-400">Consegnato</span>
              </label>
            )}
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={facoltativa} onChange={e => setFacoltativa(e.target.checked)} className="w-4 h-4 accent-gray-400" />
              <span className="text-sm text-gray-400 italic">Facoltativa</span>
            </label>
          </div>

          {/* Subtask */}
          {isEdit && (
            <div>
              <label className="text-xs text-gray-500 mb-2 block">Subtask</label>
              <div className="space-y-1 mb-2">
                {subtasks.map(sub => (
                  <div key={sub.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-800 group">
                    <button type="button" onClick={async () => {
                      const ns = sub.status === 'done' ? 'todo' : 'done'
                      await supabase.from('tasks').update({ status: ns }).eq('id', sub.id)
                      setSubtasks(prev => prev.map(s => s.id === sub.id ? { ...s, status: ns } : s))
                    }}>
                      {sub.status === 'done' ? <CheckCircle2 size={15} className="text-green-500" /> : <Circle size={15} className="text-gray-600" />}
                    </button>
                    <span className={`flex-1 text-sm ${sub.status === 'done' ? 'line-through text-gray-500' : 'text-gray-300'}`}>{sub.titolo}</span>
                    <button type="button" onClick={async () => { await supabase.from('tasks').delete().eq('id', sub.id); setSubtasks(prev => prev.filter(s => s.id !== sub.id)) }}
                      className="opacity-0 group-hover:opacity-100 text-gray-600 hover:text-red-400"><X size={13} /></button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input type="text" value={newSubtask} onChange={e => setNewSubtask(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddSubtask() } }}
                  className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-violet-500" placeholder="Aggiungi subtask..." />
                <button type="button" onClick={handleAddSubtask} disabled={!newSubtask.trim()} className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-400 hover:text-white disabled:opacity-40">
                  <Plus size={15} />
                </button>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-1">
            {isEdit && (
              <button type="button" onClick={handleDelete}
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${confirmDelete ? 'bg-red-500/20 text-red-400 border border-red-500/40 hover:bg-red-500 hover:text-white' : 'border border-gray-700 text-gray-500 hover:text-red-400'}`}>
                {confirmDelete ? 'Conferma' : 'Elimina'}
              </button>
            )}
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-lg border border-gray-700 text-gray-400 hover:text-white text-sm transition-colors">Annulla</button>
            <button type="submit" disabled={loading || !titolo.trim()} className="flex-1 py-2.5 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-medium text-sm transition-colors">
              {loading ? 'Salvataggio...' : isEdit ? 'Salva' : 'Crea'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

/* ─── ProgettoDetailPanel (drawer destro) ─── */
const ProgettoDetailPanel = ({ progetto, onClose, onEditProgetto }) => {
  const { profile: myProfile } = useAuth()
  const [tasks, setTasks] = useState([])
  const [allAssignees, setAllAssignees] = useState([])
  const [profiles, setProfiles] = useState([])
  const [myProjectRole, setMyProjectRole] = useState('editor')
  const [loadingTasks, setLoadingTasks] = useState(true)
  const [taskModal, setTaskModal] = useState(null) // null | {} | task
  const [filterStatus, setFilterStatus] = useState('all')
  const [drawerW, setDrawerW] = useState(() => parseInt(localStorage.getItem('drawerW') || '560', 10))

  const handleDrawerDragStart = useCallback((e) => {
    e.preventDefault()
    const startX = e.clientX
    const startW = drawerW
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    const onMove = (ev) => {
      const newW = Math.min(900, Math.max(360, startW - (ev.clientX - startX)))
      setDrawerW(newW)
    }
    const onUp = (ev) => {
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      const finalW = Math.min(900, Math.max(360, startW - (ev.clientX - startX)))
      setDrawerW(finalW)
      localStorage.setItem('drawerW', String(finalW))
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }, [drawerW])

  const fetchTasks = useCallback(async () => {
    const [{ data: t }, { data: ta }, { data: pf }, { data: myMember }] = await Promise.all([
      supabase.from('tasks').select('*').eq('project_id', progetto.id).order('created_at', { ascending: false }),
      supabase.from('task_assignees').select('task_id, user_id'),
      supabase.from('profiles').select('id, nickname, full_name, avatar_url').order('nickname'),
      supabase.from('project_members').select('role').eq('project_id', progetto.id).eq('user_id', myProfile?.id).single(),
    ])
    setTasks(t || [])
    setAllAssignees(ta || [])
    setProfiles(pf || [])
    setMyProjectRole(myMember?.role || 'editor')
    setLoadingTasks(false)
  }, [progetto.id, myProfile?.id])

  useEffect(() => {
    fetchTasks()
    const ch = supabase.channel(`progetto-tasks-${progetto.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks', filter: `project_id=eq.${progetto.id}` }, fetchTasks)
      .subscribe()
    return () => supabase.removeChannel(ch)
  }, [fetchTasks, progetto.id])

  const handleToggle = async (task) => {
    const newStatus = task.status === 'done' ? 'todo' : 'done'
    await supabase.from('tasks').update({ status: newStatus, updated_at: new Date().toISOString() }).eq('id', task.id)
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: newStatus } : t))
  }

  const handleSaved = (savedTask, newAssigneeIds) => {
    setTasks(prev => {
      const exists = prev.find(t => t.id === savedTask.id)
      return exists ? prev.map(t => t.id === savedTask.id ? savedTask : t) : [savedTask, ...prev]
    })
    if (newAssigneeIds !== undefined) {
      setAllAssignees(prev => [
        ...prev.filter(a => a.task_id !== savedTask.id),
        ...newAssigneeIds.map(uid => ({ task_id: savedTask.id, user_id: uid }))
      ])
    }
  }

  const handleDeleted = (id) => {
    setTasks(prev => prev.filter(t => t.id !== id && t.parent_id !== id))
  }

  // Stagista: vede solo le task a lui assegnate
  const canSeeAllTasks = myProjectRole !== 'stagista'
  const canCreateTasks = myProjectRole !== 'stagista' && myProjectRole !== 'viewer'

  const allRootTasks = tasks.filter(t => !t.parent_id)
  const rootTasks = canSeeAllTasks
    ? allRootTasks
    : allRootTasks.filter(t =>
        allAssignees.some(a => a.task_id === t.id && a.user_id === myProfile?.id)
      )
  const filtered = filterStatus === 'all' ? rootTasks : rootTasks.filter(t => t.status === filterStatus)
  const sorted = [...filtered].sort((a, b) => (PRIORITA_ORDER[a.priorita] ?? 1) - (PRIORITA_ORDER[b.priorita] ?? 1))

  const todoCnt = rootTasks.filter(t => t.status === 'todo').length
  const inpCnt  = rootTasks.filter(t => t.status === 'inprogress').length
  const doneCnt = rootTasks.filter(t => t.status === 'done').length
  // Progress sempre calcolato su tutte le root task del progetto
  const pct = allRootTasks.length ? Math.round((allRootTasks.filter(t => t.status === 'done').length / allRootTasks.length) * 100) : 0

  const STATUS_TABS = [
    { v: 'all', label: 'Tutte', count: rootTasks.length },
    { v: 'todo', label: 'Da fare', count: todoCnt },
    { v: 'inprogress', label: 'In corso', count: inpCnt },
    { v: 'done', label: 'Fatte', count: doneCnt },
  ]

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />

      {/* Drawer */}
      <div
        className="fixed right-0 top-0 h-full bg-gray-900 border-l border-gray-800 z-50 flex flex-col shadow-2xl"
        style={{ width: `min(${drawerW}px, 100vw)` }}
      >
        {/* Drag handle — bordo sinistro */}
        <div
          className="hidden sm:block absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize z-10 group"
          onMouseDown={handleDrawerDragStart}
        >
          <div className="absolute inset-y-0 left-0 right-0 group-hover:bg-violet-500/40 transition-colors rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-800 flex-shrink-0">
          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: progetto.colore }} />
          <div className="flex-1 min-w-0">
            <h2 className="text-white font-bold text-lg truncate">{progetto.titolo}</h2>
            {myProjectRole && (
              <span className={`text-[10px] font-medium ${{
                owner: 'text-violet-400', editor: 'text-blue-400',
                viewer: 'text-gray-400', stagista: 'text-orange-400',
              }[myProjectRole] || 'text-gray-500'}`}>
                {{ owner: 'Owner', editor: 'Collaboratore', viewer: 'Revisore', stagista: 'Stagista' }[myProjectRole] || myProjectRole}
              </span>
            )}
          </div>
          {(myProjectRole === 'owner' || myProjectRole === 'editor') && (
            <button onClick={() => onEditProgetto(progetto)}
              className="p-1.5 text-gray-500 hover:text-white hover:bg-gray-800 rounded-lg transition-colors" title="Modifica progetto">
              <Pencil size={15} />
            </button>
          )}
          <button onClick={onClose} className="p-1.5 text-gray-500 hover:text-white hover:bg-gray-800 rounded-lg transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Info progetto */}
        <div className="px-5 py-4 border-b border-gray-800 flex-shrink-0 space-y-3">
          {progetto.descrizione && <p className="text-gray-400 text-sm leading-relaxed">{progetto.descrizione}</p>}

          <div className="flex items-center gap-4 text-xs text-gray-500">
            {progetto.scadenza && (
              <span className="flex items-center gap-1.5">
                <Calendar size={12} /> {new Date(progetto.scadenza).toLocaleDateString('it-IT')}
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <Flag size={12} />
              {(() => {
                const s = { attivo: 'Attivo', in_pausa: 'In pausa', completato: 'Completato', archiviato: 'Archiviato' }
                return s[progetto.status] || progetto.status
              })()}
            </span>
          </div>

          {/* Progress bar */}
          {rootTasks.length > 0 && (
            <div>
              <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
                <span>Avanzamento</span>
                <span className="text-white font-medium">{pct}%</span>
              </div>
              <div className="w-full bg-gray-800 rounded-full h-1.5">
                <div className="bg-violet-500 h-1.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
              </div>
              <div className="flex items-center gap-3 mt-2 text-xs text-gray-600">
                <span><span className="text-gray-400 font-medium">{todoCnt}</span> da fare</span>
                <span><span className="text-violet-400 font-medium">{inpCnt}</span> in corso</span>
                <span><span className="text-green-400 font-medium">{doneCnt}</span> fatte</span>
              </div>
            </div>
          )}
        </div>

        {/* Filtri status + aggiungi */}
        <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-800 flex-shrink-0">
          <div className="flex items-center gap-1 flex-1 flex-wrap">
            {STATUS_TABS.map(tab => (
              <button key={tab.v} onClick={() => setFilterStatus(tab.v)}
                className={`text-xs px-2.5 py-1 rounded-full transition-colors ${filterStatus === tab.v ? 'bg-violet-600 text-white' : 'text-gray-500 hover:text-gray-300'}`}>
                {tab.label} {tab.count > 0 && <span className="ml-1 opacity-70">{tab.count}</span>}
              </button>
            ))}
          </div>
          {canCreateTasks && (
            <button onClick={() => setTaskModal({})}
              className="flex items-center gap-1.5 text-xs bg-violet-600 hover:bg-violet-500 text-white px-3 py-1.5 rounded-lg transition-colors flex-shrink-0">
              <Plus size={13} /> Nuova task
            </button>
          )}
        </div>

        {/* Lista task */}
        <div className="flex-1 overflow-y-auto px-3 py-3">
          {loadingTasks ? (
            <div className="flex items-center justify-center h-24">
              <div className="w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : sorted.length === 0 ? (
            <div className="text-center py-12">
              <Circle size={28} className="text-gray-700 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">
                {!canSeeAllTasks
                  ? 'Non hai task assegnate in questo progetto.'
                  : filterStatus === 'all' ? 'Nessuna task in questo progetto.' : 'Nessuna task con questo filtro.'}
              </p>
              {canCreateTasks && filterStatus === 'all' && (
                <button onClick={() => setTaskModal({})} className="text-violet-400 hover:text-violet-300 text-xs mt-2 transition-colors">
                  + Aggiungi la prima task
                </button>
              )}
            </div>
          ) : (
            <div>
              {sorted.map(task => (
                <div key={task.id}>
                  <TaskRowDetail
                    task={task}
                    allTasks={tasks}
                    allAssignees={allAssignees}
                    profiles={profiles}
                    onToggle={handleToggle}
                    onEdit={t => setTaskModal(t)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Task modal */}
      {taskModal !== null && (
        <TaskModalInline
          task={taskModal?.id ? taskModal : null}
          progettoId={progetto.id}
          profiles={profiles}
          onClose={() => setTaskModal(null)}
          onSaved={handleSaved}
          onDeleted={handleDeleted}
        />
      )}
    </>
  )
}

const STATUS_OPTIONS = [
  { value: 'attivo', label: 'Attivo', color: 'bg-green-500' },
  { value: 'in_pausa', label: 'In pausa', color: 'bg-yellow-500' },
  { value: 'completato', label: 'Completato', color: 'bg-blue-500' },
  { value: 'archiviato', label: 'Archiviato', color: 'bg-gray-500' },
]

// Modale unificato: crea o modifica progetto
const ModalProgetto = ({ progetto, onClose, onSaved, onDeleted }) => {
  const { profile } = useAuth()
  const isEdit = !!progetto?.id
  const [titolo, setTitolo] = useState(progetto?.titolo || '')
  const [descrizione, setDescrizione] = useState(progetto?.descrizione || '')
  const [scadenza, setScadenza] = useState(progetto?.scadenza || '')
  const [colore, setColore] = useState(progetto?.colore || '#7c3aed')
  const [status, setStatus] = useState(progetto?.status || 'attivo')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [allProfiles, setAllProfiles] = useState([])
  const [selectedMembers, setSelectedMembers] = useState([])

  // selectedMembers è un array di { uid, role }
  const toggleMember = (uid) => {
    setSelectedMembers(prev => {
      const exists = prev.find(m => m.uid === uid)
      if (exists) return prev.filter(m => m.uid !== uid)
      return [...prev, { uid, role: 'editor' }]
    })
  }
  const setMemberRole = (uid, role) => {
    setSelectedMembers(prev => prev.map(m => m.uid === uid ? { ...m, role } : m))
  }

  useEffect(() => {
    supabase.from('profiles').select('id, nickname, full_name, avatar_url, role').order('nickname')
      .then(({ data }) => setAllProfiles((data || []).filter(p => p.id !== profile?.id)))
    // In edit mode pre-carica i membri già presenti con i loro ruoli
    if (isEdit) {
      supabase.from('project_members').select('user_id, role').eq('project_id', progetto.id)
        .then(({ data }) => setSelectedMembers(
          (data || [])
            .filter(m => m.user_id !== profile?.id)
            .map(m => ({ uid: m.user_id, role: m.role || 'editor' }))
        ))
    }
  }, [isEdit, progetto?.id, profile?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    const payload = { titolo, descrizione, scadenza: scadenza || null, colore, status }
    let data, err
    if (isEdit) {
      ;({ data, error: err } = await supabase.from('progetti').update(payload).eq('id', progetto.id).select('*, profiles!progetti_created_by_fkey(nickname, full_name, avatar_url)').single())
      if (!err) {
        // Sincronizza membri (escludi il creator/owner corrente)
        await supabase.from('project_members').delete().eq('project_id', progetto.id).neq('user_id', profile.id)
        if (selectedMembers.length) {
          await supabase.from('project_members').insert(
            selectedMembers.map(m => ({ project_id: progetto.id, user_id: m.uid, role: m.role }))
          )
        }
      }
    } else {
      ;({ data, error: err } = await supabase.from('progetti').insert({ ...payload, created_by: profile.id }).select('*, profiles!progetti_created_by_fkey(nickname, full_name, avatar_url)').single())
      if (!err) {
        const inserts = [
          { project_id: data.id, user_id: profile.id, role: 'owner' },
          ...selectedMembers.map(m => ({ project_id: data.id, user_id: m.uid, role: m.role })),
        ]
        await supabase.from('project_members').insert(inserts)
      }
    }
    if (err) { setError(err.message); setLoading(false); return }
    onSaved(data, isEdit)
    onClose()
  }

  const handleDelete = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return }
    await supabase.from('progetti').delete().eq('id', progetto.id)
    onDeleted(progetto.id)
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4 py-6">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gray-900 border-b border-gray-800 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <h2 className="text-lg font-bold text-white">{isEdit ? 'Modifica progetto' : 'Nuovo progetto'}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Titolo *</label>
            <input
              type="text"
              value={titolo}
              onChange={(e) => setTitolo(e.target.value)}
              required
              autoFocus
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition-colors"
              placeholder="Nome del progetto"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Descrizione</label>
            <textarea
              value={descrizione}
              onChange={(e) => setDescrizione(e.target.value)}
              rows={3}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition-colors resize-none"
              placeholder="Di cosa si tratta..."
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Scadenza</label>
              <input
                type="date"
                value={scadenza}
                onChange={(e) => setScadenza(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition-colors"
              >
                {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>

          <ColorPicker value={colore} onChange={setColore} />

          {/* Membri del team */}
          {allProfiles.length > 0 && (
            <div>
              <label className="block text-sm text-gray-400 mb-2">Team</label>
              <div className="flex flex-col gap-1.5">
                {allProfiles.map(p => {
                  const member = selectedMembers.find(m => m.uid === p.id)
                  const sel = !!member
                  const nome = p.nickname || p.full_name || 'Utente'
                  return (
                    <div key={p.id} className={`flex items-center rounded-lg border overflow-hidden transition-colors ${
                      sel ? 'border-violet-500/60' : 'border-gray-800'
                    }`}>
                      {/* Chip persona — click per selezionare/deselezionare */}
                      <button
                        type="button"
                        onClick={() => toggleMember(p.id)}
                        className={`flex items-center gap-2 px-2.5 py-2 flex-1 text-xs transition-colors ${
                          sel
                            ? 'bg-violet-600/15 text-violet-200'
                            : 'bg-gray-800/40 text-gray-400 hover:bg-gray-800 hover:text-gray-300'
                        }`}
                      >
                        <div className="w-5 h-5 rounded-full bg-violet-600 flex items-center justify-center text-white text-[10px] font-bold overflow-hidden flex-shrink-0">
                          {p.avatar_url
                            ? <img src={p.avatar_url} alt="" className="w-full h-full object-cover" />
                            : nome[0].toUpperCase()}
                        </div>
                        <span className="flex-1 text-left truncate">{nome}</span>
                        {sel && <Check size={11} className="text-violet-400 flex-shrink-0" />}
                      </button>
                      {/* Selettore ruolo — visibile solo se selezionato */}
                      {sel && (
                        <select
                          value={member.role}
                          onChange={e => { e.stopPropagation(); setMemberRole(p.id, e.target.value) }}
                          onClick={e => e.stopPropagation()}
                          className="text-[11px] bg-gray-800 border-l border-violet-500/30 px-2.5 py-2 text-indigo-300 focus:outline-none cursor-pointer appearance-none"
                        >
                          <option value="editor">Collaboratore</option>
                          <option value="viewer">Revisore</option>
                          <option value="stagista">Stagista</option>
                        </select>
                      )}
                    </div>
                  )
                })}
              </div>
              {selectedMembers.length > 0 && (
                <p className="text-xs text-gray-600 mt-1.5">
                  {selectedMembers.length} membro{selectedMembers.length !== 1 ? 'i' : ''} nel team
                </p>
              )}
            </div>
          )}

          {error && <p className="text-red-400 text-sm">{error}</p>}

          <div className="flex gap-3 pt-2">
            {isEdit && (
              <button type="button" onClick={handleDelete}
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  confirmDelete
                    ? 'bg-red-500/20 text-red-400 border border-red-500/40 hover:bg-red-500 hover:text-white'
                    : 'border border-gray-700 text-gray-500 hover:text-red-400 hover:border-red-500/40'
                }`}>
                {confirmDelete ? 'Conferma' : 'Elimina'}
              </button>
            )}
            <button type="button" onClick={onClose} className="flex-1 py-3 rounded-lg border border-gray-700 text-gray-400 hover:text-white transition-colors text-sm">
              Annulla
            </button>
            <button type="submit" disabled={loading || !titolo.trim()} className="flex-1 py-3 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-medium text-sm transition-colors">
              {loading ? 'Salvataggio...' : isEdit ? 'Salva' : 'Crea progetto'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const ModalInviti = ({ progetto, onClose }) => {
  const { profile: myProfile } = useAuth()
  const [utenti, setUtenti] = useState([])
  const [membriProgetto, setMembriProgetto] = useState([])
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState(null)
  const [removing, setRemoving] = useState(null)
  const [copiedLink, setCopiedLink] = useState(false)

  useEffect(() => {
    fetchDati()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDati = async () => {
    const [{ data: tutti }, { data: membri }] = await Promise.all([
      supabase.from('profiles').select('*').order('nickname'),
      supabase.from('project_members').select('*, profiles(*)').eq('project_id', progetto.id)
    ])
    setUtenti(tutti || [])
    setMembriProgetto(membri || [])
    setLoading(false)
  }

  const getMembro = (userId) => membriProgetto.find(m => m.user_id === userId)

  const handleAdd = async (userId) => {
    setAdding(userId)
    await supabase.from('project_members').insert({ project_id: progetto.id, user_id: userId, role: 'viewer' })
    await fetchDati()
    setAdding(null)
  }

  const handleRoleChange = async (userId, newRole) => {
    await supabase.from('project_members').update({ role: newRole }).eq('project_id', progetto.id).eq('user_id', userId)
    await fetchDati()
  }

  const handleRemove = async (userId) => {
    setRemoving(userId)
    await supabase.from('project_members').delete().eq('project_id', progetto.id).eq('user_id', userId)
    await fetchDati()
    setRemoving(null)
  }

  const handleCopyLink = async () => {
    const { data } = await supabase
      .from('project_invites')
      .insert({ project_id: progetto.id, created_by: myProfile.id, role: 'viewer' })
      .select()
      .single()
    if (data) {
      const url = `${window.location.origin}/invito/${data.token}`
      navigator.clipboard.writeText(url)
      setCopiedLink(true)
      setTimeout(() => setCopiedLink(false), 2000)
    }
  }

  const utentiFiltrati = utenti.filter(u => u.id !== myProfile?.id)

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-bold text-white">Gestisci membri</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>
        <p className="text-gray-500 text-sm mb-6">{progetto.titolo}</p>

        {/* Link invito esterno */}
        <button
          onClick={handleCopyLink}
          className="w-full flex items-center gap-3 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-xl px-4 py-3 mb-6 transition-colors"
        >
          {copiedLink ? <Check size={16} className="text-green-400" /> : <Link size={16} className="text-violet-400" />}
          <span className="text-sm text-gray-300">
            {copiedLink ? 'Link copiato!' : 'Copia link invito (per utenti esterni)'}
          </span>
        </button>

        {/* Lista utenti */}
        <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Membri registrati</p>
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {utentiFiltrati.map((u) => {
              const membro = getMembro(u.id)
              const isMe = u.id === myProfile?.id
              const nome = u.nickname || u.full_name || u.full_name || 'Utente'
              const lettera = nome[0].toUpperCase()

              return (
                <div key={u.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-800 transition-colors">
                  <div className="w-9 h-9 rounded-full bg-violet-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0 overflow-hidden">
                    {u.avatar_url
                      ? <img src={u.avatar_url} alt="avatar" className="w-full h-full object-cover" />
                      : lettera}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{nome}{isMe && <span className="text-gray-600 text-xs ml-1">(tu)</span>}</p>
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      {u.role === 'admin' ? <Shield size={10} /> : <User size={10} />}
                      {u.role}
                    </div>
                  </div>
                  {membro ? (
                    <div className="flex items-center gap-2">
                      {/* Selettore ruolo */}
                      <select
                        value={membro.role}
                        onChange={e => handleRoleChange(u.id, e.target.value)}
                        className="text-xs bg-gray-800 border border-gray-700 rounded-lg px-2 py-1 text-gray-300 focus:outline-none focus:border-violet-500 cursor-pointer"
                      >
                        <option value="owner">Owner</option>
                        <option value="editor">Collaboratore</option>
                        <option value="viewer">Revisore</option>
                        <option value="stagista">Stagista</option>
                      </select>
                      {!isMe && (
                        <button
                          onClick={() => handleRemove(u.id)}
                          disabled={removing === u.id}
                          className="text-xs px-2.5 py-1 rounded-lg bg-gray-700 hover:bg-red-500/20 hover:text-red-400 text-gray-500 transition-colors disabled:opacity-50"
                        >
                          {removing === u.id ? '...' : 'Rimuovi'}
                        </button>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => handleAdd(u.id)}
                      disabled={adding === u.id}
                      className="text-xs px-3 py-1.5 rounded-lg bg-violet-600/20 hover:bg-violet-600 text-violet-400 hover:text-white transition-colors disabled:opacity-50 flex items-center gap-1"
                    >
                      <UserPlus size={12} />
                      {adding === u.id ? '...' : 'Aggiungi'}
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

/* ─── ProgettoCard ─── */
const ProgettoCard = ({ p, onOpen, onEdit, onInviti, onStatusChange, showCreator = false }) => {
  const [expanded, setExpanded] = useState(false)
  const scadenzaDate = p.scadenza ? new Date(p.scadenza + 'T00:00:00') : null
  const oggi = new Date(); oggi.setHours(0, 0, 0, 0)
  const scaduta = scadenzaDate && scadenzaDate < oggi && p.status !== 'completato' && p.status !== 'archiviato'
  const allMembers = (p.project_members || []).filter(m => m.profiles)
  const members = allMembers.slice(0, 5)
  const extraMembers = allMembers.length - members.length
  const roleColors = { owner: 'text-violet-400', editor: 'text-blue-400', viewer: 'text-gray-400', stagista: 'text-orange-400' }
  const roleLabels = { owner: 'Owner', editor: 'Collaboratore', viewer: 'Revisore', stagista: 'Stagista' }

  return (
    <div
      className={`bg-gray-900 border rounded-2xl overflow-hidden transition-all group ${
        p.status === 'archiviato' ? 'border-gray-800/60 opacity-60' : 'border-gray-800 hover:border-gray-700'
      }`}
    >
      {/* Riga principale — sempre visibile */}
      <div
        onClick={() => onOpen(p)}
        className="flex items-center gap-2 px-4 py-3 cursor-pointer hover:bg-gray-800/30 transition-colors"
      >
        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: p.colore }} />
        <div className="flex-1 min-w-0">
          <h3 className="text-white text-sm font-semibold truncate group-hover:text-violet-300 transition-colors">{p.titolo}</h3>
          {showCreator && p.profiles && (
            <p className="text-[10px] text-gray-600 truncate">
              di {p.profiles.nickname || p.profiles.full_name || '—'}
            </p>
          )}
        </div>

        {/* Scadenza inline */}
        {p.scadenza && (
          <span className={`flex items-center gap-1 text-[11px] flex-shrink-0 ${scaduta ? 'text-red-400' : 'text-gray-600'}`}>
            <Calendar size={10} />
            {new Date(p.scadenza + 'T00:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })}
          </span>
        )}

        {/* Avatar stack */}
        {members.length > 0 && (
          <div className="flex -space-x-1.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
            {members.map((m, i) => {
              const mp = m.profiles
              const nome = mp.nickname || mp.full_name || '?'
              return (
                <div key={mp.id} title={nome} style={{ zIndex: members.length - i }}
                  className="w-5 h-5 rounded-full bg-violet-700 border-2 border-gray-900 flex items-center justify-center text-white text-[9px] font-bold overflow-hidden">
                  {mp.avatar_url ? <img src={mp.avatar_url} alt="" className="w-full h-full object-cover" /> : nome[0].toUpperCase()}
                </div>
              )
            })}
            {extraMembers > 0 && (
              <div className="w-5 h-5 rounded-full bg-gray-700 border-2 border-gray-900 flex items-center justify-center text-gray-400 text-[9px] font-bold">+{extraMembers}</div>
            )}
          </div>
        )}

        {/* Expand toggle */}
        <button
          onClick={e => { e.stopPropagation(); setExpanded(v => !v) }}
          className={`p-1 rounded-lg transition-colors flex-shrink-0 ${expanded ? 'text-violet-400 bg-violet-500/10' : 'text-gray-700 hover:text-gray-400 hover:bg-gray-800'}`}
          title={expanded ? 'Comprimi' : 'Espandi'}
        >
          <ChevronDown size={14} className={`transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Dettagli espansi */}
      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-gray-800/60 space-y-3">

          {/* Descrizione */}
          {p.descrizione && (
            <p className="text-gray-400 text-xs leading-relaxed">{p.descrizione}</p>
          )}

          {/* Status + azioni */}
          <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
            <select
              value={p.status}
              onChange={e => onStatusChange(p.id, e.target.value)}
              className="text-xs bg-gray-800 border border-gray-700/50 rounded-lg px-2 py-1 text-gray-400 focus:outline-none cursor-pointer appearance-none"
            >
              {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <div className="flex-1" />
            <button onClick={() => onInviti(p)} className="p-1.5 rounded-lg text-gray-600 hover:text-violet-400 hover:bg-gray-800 transition-colors" title="Gestisci membri">
              <UserPlus size={13} />
            </button>
            <button onClick={() => onEdit(p)} className="p-1.5 rounded-lg text-gray-600 hover:text-white hover:bg-gray-800 transition-colors" title="Modifica">
              <Pencil size={13} />
            </button>
          </div>

          {/* Team */}
          {allMembers.length > 0 && (
            <div className="space-y-1.5">
              {allMembers.map(m => {
                const mp = m.profiles
                if (!mp) return null
                const nome = mp.nickname || mp.full_name || 'Utente'
                return (
                  <div key={mp.id} className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-violet-700 flex items-center justify-center text-white text-[9px] font-bold overflow-hidden flex-shrink-0">
                      {mp.avatar_url ? <img src={mp.avatar_url} alt="" className="w-full h-full object-cover" /> : nome[0].toUpperCase()}
                    </div>
                    <span className="text-gray-400 text-xs flex-1 truncate">{nome}</span>
                    <span className={`text-[10px] font-medium ${roleColors[m.role] || 'text-gray-600'}`}>{roleLabels[m.role] || m.role}</span>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/* ─── StatusGroup ─── */
const STATUS_GROUPS = [
  { key: 'attivo',      label: 'Attivi',      dot: 'bg-green-500',  defaultOpen: true  },
  { key: 'in_pausa',    label: 'In pausa',    dot: 'bg-yellow-500', defaultOpen: true  },
  { key: 'completato',  label: 'Completati',  dot: 'bg-blue-500',   defaultOpen: false },
  { key: 'archiviato',  label: 'Archiviati',  dot: 'bg-gray-500',   defaultOpen: false },
]

const StatusGroup = ({ group, progetti, onOpen, onEdit, onInviti, onStatusChange, showCreator = false }) => {
  const [open, setOpen] = useState(group.defaultOpen)
  if (!progetti.length) return null

  return (
    <div>
      {/* Intestazione gruppo */}
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-2.5 py-2 px-1 group/hdr"
      >
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${group.dot}`} />
        <span className="text-sm font-semibold text-gray-300 group-hover/hdr:text-white transition-colors">
          {group.label}
        </span>
        <span className="text-xs text-gray-600 bg-gray-800 px-2 py-0.5 rounded-full">{progetti.length}</span>
        <div className="flex-1 h-px bg-gray-800 mx-1" />
        {open
          ? <ChevronDown size={14} className="text-gray-600 group-hover/hdr:text-gray-400 transition-colors flex-shrink-0" />
          : <ChevronRight size={14} className="text-gray-600 group-hover/hdr:text-gray-400 transition-colors flex-shrink-0" />}
      </button>

      {/* Griglia card */}
      {open && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mt-2 mb-1">
          {progetti.map(p => (
            <ProgettoCard
              key={p.id}
              p={p}
              onOpen={onOpen}
              onEdit={onEdit}
              onInviti={onInviti}
              onStatusChange={onStatusChange}
              showCreator={showCreator}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/* ─── Sezione "Altri progetti" (admin only, collassata) ─── */
const STATUS_GROUPS_COLLAPSED = STATUS_GROUPS.map(g => ({ ...g, defaultOpen: false }))

const ADMIN_PWD = 'alternative'

const AltriProgettiSection = ({ progetti, onOpen, onEdit, onInviti, onStatusChange }) => {
  const [open, setOpen] = useState(false)
  const [unlocked, setUnlocked] = useState(false)
  const [pwdInput, setPwdInput] = useState('')
  const [showPwd, setShowPwd] = useState(false)
  const [pwdError, setPwdError] = useState(false)

  if (!progetti.length) return null
  const attivi = progetti.filter(p => p.status === 'attivo').length

  const handleToggle = () => {
    if (unlocked) { setOpen(v => !v); return }
    setOpen(v => !v)
  }

  const handleUnlock = (e) => {
    e.preventDefault()
    if (pwdInput === ADMIN_PWD) {
      setUnlocked(true)
      setPwdError(false)
      setPwdInput('')
    } else {
      setPwdError(true)
      setPwdInput('')
    }
  }

  return (
    <div className="mt-6">
      {/* Separatore visivo */}
      <div className="flex items-center gap-3 mb-1">
        <div className="flex-1 h-px bg-gray-800" />
        <span className="text-[10px] text-gray-700 uppercase tracking-widest font-semibold">Admin view</span>
        <div className="flex-1 h-px bg-gray-800" />
      </div>

      <button
        onClick={handleToggle}
        className="w-full flex items-center gap-2.5 py-2.5 px-3 rounded-xl hover:bg-gray-900 group/hdr transition-colors"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Lock size={13} className={`flex-shrink-0 ${unlocked ? 'text-green-500' : 'text-gray-600'}`} />
          <span className="text-sm font-semibold text-gray-500 group-hover/hdr:text-gray-300 transition-colors">
            Tutti gli altri progetti
          </span>
          <span className="text-xs text-gray-700 bg-gray-800/80 px-2 py-0.5 rounded-full">{progetti.length} totali</span>
          {attivi > 0 && (
            <span className="text-xs text-green-600 bg-green-500/10 px-2 py-0.5 rounded-full">{attivi} attivi</span>
          )}
        </div>
        {open
          ? <ChevronDown size={14} className="text-gray-600 flex-shrink-0" />
          : <ChevronRight size={14} className="text-gray-600 flex-shrink-0" />}
      </button>

      {open && !unlocked && (
        <div className="mt-2 mx-1 p-4 bg-gray-900 border border-gray-800 rounded-xl">
          <div className="flex items-center gap-2 mb-3">
            <Lock size={15} className="text-gray-500" />
            <p className="text-sm text-gray-400">Inserisci la password per accedere</p>
          </div>
          <form onSubmit={handleUnlock} className="flex items-center gap-2">
            <div className="relative flex-1">
              <input
                autoFocus
                type={showPwd ? 'text' : 'password'}
                value={pwdInput}
                onChange={e => { setPwdInput(e.target.value); setPwdError(false) }}
                placeholder="Password..."
                className={`w-full bg-gray-800 border rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none pr-9 transition-colors ${
                  pwdError ? 'border-red-500/60 focus:border-red-500' : 'border-gray-700 focus:border-violet-500'
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPwd(v => !v)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-400 transition-colors"
              >
                {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <button
              type="submit"
              className="px-3 py-2 bg-violet-600 hover:bg-violet-500 text-white text-sm rounded-lg transition-colors flex-shrink-0"
            >
              Accedi
            </button>
          </form>
          {pwdError && (
            <p className="text-red-400 text-xs mt-2 flex items-center gap-1">
              <X size={11} /> Password errata
            </p>
          )}
        </div>
      )}

      {open && unlocked && (
        <div className="mt-1 pl-2 border-l-2 border-gray-800 ml-1 space-y-3">
          {STATUS_GROUPS_COLLAPSED.map(group => (
            <StatusGroup
              key={group.key}
              group={group}
              progetti={progetti.filter(p => p.status === group.key)}
              onOpen={onOpen}
              onEdit={onEdit}
              onInviti={onInviti}
              onStatusChange={onStatusChange}
              showCreator={true}
            />
          ))}
        </div>
      )}
    </div>
  )
}

const Progetti = () => {
  const { profile } = useAuth()
  const [progetti, setProgetti] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalProgetto, setModalProgetto] = useState(null)
  const [progettoInviti, setProgettoInviti] = useState(null)
  const [progettoDettaglio, setProgettoDettaglio] = useState(null)

  const isAdmin = profile?.role === 'admin'

  useEffect(() => {
    fetchProgetti()
    const channel = supabase
      .channel('progetti-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'progetti' }, fetchProgetti)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'project_members' }, fetchProgetti)
      .subscribe()
    return () => supabase.removeChannel(channel)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProgetti = async () => {
    const { data } = await supabase
      .from('progetti')
      .select('*, profiles!progetti_created_by_fkey(nickname, full_name, avatar_url), project_members(user_id, profiles(id, nickname, full_name, avatar_url))')
      .order('created_at', { ascending: false })
    setProgetti(data || [])
    setLoading(false)
  }

  const handleSaved = (saved, isEdit) => {
    setProgetti(prev =>
      isEdit ? prev.map(p => p.id === saved.id ? saved : p) : [saved, ...prev]
    )
  }

  const handleDeleted = (id) => setProgetti(prev => prev.filter(p => p.id !== id))

  const handleStatusChange = async (id, newStatus) => {
    await supabase.from('progetti').update({ status: newStatus }).eq('id', id)
    setProgetti(prev => prev.map(p => p.id === id ? { ...p, status: newStatus } : p))
  }

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
    </div>
  )

  // Per gli admin: dividi tra "i miei" e "tutti gli altri"
  const miei = isAdmin
    ? progetti.filter(p => p.project_members?.some(m => m.user_id === profile.id))
    : progetti
  const altri = isAdmin
    ? progetti.filter(p => !p.project_members?.some(m => m.user_id === profile.id))
    : []

  const totale = progetti.length
  const sharedProps = {
    onOpen: setProgettoDettaglio,
    onEdit: setModalProgetto,
    onInviti: setProgettoInviti,
    onStatusChange: handleStatusChange,
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Progetti</h1>
          <p className="text-gray-500 text-sm mt-1">
            {isAdmin
              ? `${miei.length} tuoi · ${altri.length} altri`
              : `${totale} ${totale === 1 ? 'progetto' : 'progetti'}`}
          </p>
        </div>
        <button
          onClick={() => setModalProgetto({})}
          className="flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white font-medium px-4 py-2.5 rounded-lg transition-colors text-sm"
        >
          <Plus size={16} /> Nuovo
        </button>
      </div>

      {totale === 0 ? (
        <div className="text-center py-24">
          <p className="text-gray-600 mb-4">Nessun progetto ancora.</p>
          <button onClick={() => setModalProgetto({})} className="text-violet-400 hover:text-violet-300 text-sm transition-colors">
            Crea il primo progetto →
          </button>
        </div>
      ) : (
        <div>
          {/* I miei progetti (o tutti per non-admin) */}
          {isAdmin && miei.length === 0 && (
            <p className="text-gray-600 text-sm mb-4 italic">Non sei ancora membro di nessun progetto.</p>
          )}
          <div className="space-y-3">
            {STATUS_GROUPS.map(group => (
              <StatusGroup
                key={group.key}
                group={group}
                progetti={miei.filter(p => p.status === group.key)}
                {...sharedProps}
              />
            ))}
          </div>

          {/* Tutti gli altri (admin only) */}
          {isAdmin && (
            <AltriProgettiSection progetti={altri} {...sharedProps} />
          )}
        </div>
      )}

      {modalProgetto !== null && (
        <ModalProgetto
          progetto={modalProgetto?.id ? modalProgetto : null}
          onClose={() => setModalProgetto(null)}
          onSaved={handleSaved}
          onDeleted={handleDeleted}
        />
      )}

      {progettoInviti && (
        <ModalInviti
          progetto={progettoInviti}
          onClose={() => setProgettoInviti(null)}
        />
      )}

      {progettoDettaglio && (
        <ProgettoDetailPanel
          progetto={progettoDettaglio}
          onClose={() => setProgettoDettaglio(null)}
          onEditProgetto={(p) => { setProgettoDettaglio(null); setModalProgetto(p) }}
        />
      )}
    </div>
  )
}

export default Progetti